import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SitemapPage() {
  const [locations, setLocations] = useState([]);
  const [servicePages, setServicePages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, svcRes] = await Promise.all([
          fetch(`${API}/locations`).then(r => r.json()),
          fetch(`${API}/service-pages/public`).then(r => r.json()).catch(() => [])
        ]);
        setLocations(locRes || []);
        setServicePages(svcRes || []);
      } catch (e) {
        console.error("Sitemap fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Group service pages by city
  const servicesByCity = {};
  servicePages.forEach(p => {
    const city = p.city || "General";
    if (!servicesByCity[city]) servicesByCity[city] = [];
    servicesByCity[city].push(p);
  });

  // Group locations by county
  const locationsByCounty = {};
  locations.forEach(l => {
    const county = l.county || "Other";
    if (!locationsByCounty[county]) locationsByCounty[county] = [];
    locationsByCounty[county].push(l);
  });

  const corePages = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/services", label: "Services" },
    { path: "/locations", label: "Service Areas" },
    { path: "/gallery", label: "Gallery" },
    { path: "/testimonials", label: "Testimonials" },
    { path: "/blog", label: "Blog" },
    { path: "/contact", label: "Contact" },
    { path: "/sms-policy", label: "SMS Policy" },
  ];

  return (
    <>
      <SEOHead
        title="Sitemap | Overhaul Ops LLC"
        description="Complete sitemap of Overhaul Ops LLC - Firefighter-owned junk removal serving DFW Texas. Find all our service areas, services, and pages."
        canonicalUrl="https://overhaulops.com/sitemap"
      />
      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h1
            className="text-4xl font-bold uppercase tracking-tight mb-2 text-[#1a1a1a]"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            Sitemap
          </h1>
          <p className="text-gray-500 mb-10">All pages on overhaulops.com</p>

          {loading ? (
            <p className="text-gray-400">Loading...</p>
          ) : (
            <div className="space-y-10">
              {/* Core Pages */}
              <section>
                <h2
                  className="text-xl font-bold uppercase tracking-tight mb-4 text-[#b51a00] border-b pb-2"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                >
                  Main Pages
                </h2>
                <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {corePages.map(p => (
                    <li key={p.path}>
                      <Link to={p.path} className="text-gray-700 hover:text-[#b51a00] transition-colors text-sm">
                        {p.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Location Pages */}
              <section>
                <h2
                  className="text-xl font-bold uppercase tracking-tight mb-4 text-[#b51a00] border-b pb-2"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                >
                  Service Areas ({locations.length} Cities)
                </h2>
                {Object.entries(locationsByCounty).sort().map(([county, locs]) => (
                  <div key={county} className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">{county} County</h3>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {locs.map(l => (
                        <li key={l.slug}>
                          <Link
                            to={`/locations/${l.slug}`}
                            className="text-gray-700 hover:text-[#b51a00] transition-colors text-sm"
                          >
                            {l.city}, TX
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>

              {/* Service Landing Pages */}
              {Object.keys(servicesByCity).length > 0 && (
                <section>
                  <h2
                    className="text-xl font-bold uppercase tracking-tight mb-4 text-[#b51a00] border-b pb-2"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                  >
                    Service Pages ({servicePages.length} Pages)
                  </h2>
                  {Object.entries(servicesByCity).sort().map(([city, pages]) => (
                    <div key={city} className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">{city}</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {pages.map(p => (
                          <li key={p.slug}>
                            <Link
                              to={`/services/${p.slug}`}
                              className="text-gray-700 hover:text-[#b51a00] transition-colors text-sm"
                            >
                              {p.title || p.slug}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
